import type { CustomerCardDto } from '@deli/contracts';
import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import {
  useAddAddress,
  useAddContact,
  useChangeStatus,
  useCustomerCard,
  useRemoveAddress,
  useRemoveCompany,
  useRemoveContact,
  useSaveCompany,
  useUpdateAddress,
  useUpdateCustomer,
} from '../api/customers.js';
import { useOrders } from '../api/orders.js';
import { useCurrentUser } from '../auth/session.js';
import { ErrorText } from '../components/ErrorText.js';
import { QueryState } from '../components/QueryState.js';
import { formatDateTime } from '../format.js';
import { t } from '../i18n/uk.js';
import { CustomerLoyalty } from './CustomerLoyalty.js';
import { CustomerPricing } from './CustomerPricing.js';
import { OrdersTable } from './OrdersPage.js';
import { statusBadge } from './CustomersPage.js';

const tc = t.customers;
type Card = CustomerCardDto;

/** A text input bound to one key of a form-state object. */
function Field<T extends Record<string, unknown>>({
  form,
  set,
  name,
  label,
  required,
  type = 'text',
}: {
  form: T;
  set: (next: T) => void;
  name: keyof T & string;
  label: string;
  required?: boolean;
  type?: string;
}) {
  return (
    <label>
      {label}
      <input
        aria-label={label}
        type={type}
        required={required}
        value={String(form[name] ?? '')}
        onChange={(e) => set({ ...form, [name]: e.target.value })}
      />
    </label>
  );
}

function DetailsForm({ card }: { card: Card }) {
  const update = useUpdateCustomer(card.id);
  const [form, setForm] = useState({
    fullName: card.fullName,
    phone: card.phone ?? '',
    email: card.email ?? '',
    type: card.type,
    loyaltyEnabled: card.loyaltyEnabled,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    update.mutate({
      ...form,
      phone: form.phone || null,
      email: form.email || null,
      // On a type change the API applies the new type's default (B2B never earns Q-points).
      loyaltyEnabled: form.type === card.type ? form.loyaltyEnabled : undefined,
    });
  };
  return (
    <form className="card form-grid" onSubmit={submit}>
      <h2>{tc.details}</h2>
      <Field form={form} set={setForm} name="fullName" label={tc.name} required />
      <Field form={form} set={setForm} name="phone" label={tc.phone} type="tel" />
      <Field form={form} set={setForm} name="email" label={tc.email} type="email" />
      <label>
        {tc.type}
        <select
          aria-label={tc.type}
          value={form.type}
          onChange={(e) => setForm({ ...form, type: e.target.value as Card['type'] })}
        >
          <option value="b2c">{tc.types.b2c}</option>
          <option value="b2b">{tc.types.b2b}</option>
        </select>
      </label>
      {form.type === 'b2c' ? (
        <label className="check">
          <input
            type="checkbox"
            checked={form.loyaltyEnabled}
            onChange={(e) => setForm({ ...form, loyaltyEnabled: e.target.checked })}
          />
          {tc.loyalty}
        </label>
      ) : (
        <p className="muted">{tc.loyaltyB2bNote}</p>
      )}
      <div className="actions">
        <button type="submit" className="primary" disabled={update.isPending}>
          {t.common.save}
        </button>
        {update.isSuccess && <span className="ok-text">{t.common.saved}</span>}
      </div>
      <ErrorText error={update.error} />
    </form>
  );
}

/** Block / unblock, or approve / reject a pending registration — always with a reason. */
function StatusActions({ card }: { card: Card }) {
  const change = useChangeStatus(card.id);
  const [reason, setReason] = useState('');
  const targets: [Card['status'], string][] =
    card.status === 'pending_approval'
      ? [
          ['active', tc.approve],
          ['rejected', tc.reject],
        ]
      : card.status === 'active'
        ? [['blocked', tc.block]]
        : card.status === 'blocked'
          ? [['active', tc.unblock]]
          : [];
  if (targets.length === 0) return null;
  return (
    <form className="inline-form" onSubmit={(e) => e.preventDefault()}>
      <input
        aria-label={t.common.reason}
        placeholder={t.common.reason}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      {targets.map(([status, label]) => (
        <button
          key={status}
          type="button"
          disabled={!reason.trim() || change.isPending}
          onClick={() => change.mutate({ status, reason }, { onSuccess: () => setReason('') })}
        >
          {label}
        </button>
      ))}
      <ErrorText error={change.error} />
    </form>
  );
}

function CompanySection({ card }: { card: Card }) {
  const save = useSaveCompany(card.id);
  const remove = useRemoveCompany(card.id);
  const [form, setForm] = useState({
    legalName: card.company?.legalName ?? '',
    edrpou: card.company?.edrpou ?? '',
    ipn: card.company?.ipn ?? '',
    legalAddress: card.company?.legalAddress ?? '',
    iban: card.company?.iban ?? '',
    vatPayer: card.company?.vatPayer ?? false,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate(form);
  };
  return (
    <form className="card form-grid" onSubmit={submit}>
      <h2>{tc.companyTitle}</h2>
      <Field form={form} set={setForm} name="legalName" label={tc.legalName} required />
      <Field form={form} set={setForm} name="edrpou" label={tc.edrpou} />
      <Field form={form} set={setForm} name="ipn" label={tc.ipn} />
      <Field form={form} set={setForm} name="iban" label={tc.iban} />
      <Field form={form} set={setForm} name="legalAddress" label={tc.legalAddress} />
      <label className="check">
        <input
          type="checkbox"
          checked={form.vatPayer}
          onChange={(e) => setForm({ ...form, vatPayer: e.target.checked })}
        />
        {tc.vatPayer}
      </label>
      <div className="actions">
        <button type="submit" className="primary" disabled={save.isPending}>
          {t.common.save}
        </button>
        {card.company && (
          <button type="button" disabled={remove.isPending} onClick={() => remove.mutate()}>
            {tc.removeCompany}
          </button>
        )}
        {save.isSuccess && <span className="ok-text">{t.common.saved}</span>}
      </div>
      <ErrorText error={save.error ?? remove.error} />
    </form>
  );
}

function ContactsSection({ card }: { card: Card }) {
  const add = useAddContact(card.id);
  const remove = useRemoveContact(card.id);
  const empty = { name: '', role: '', phone: '', email: '' };
  const [form, setForm] = useState(empty);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    add.mutate(form, { onSuccess: () => setForm(empty) });
  };
  return (
    <div className="card">
      <h2>{tc.contactsTitle}</h2>
      {card.contacts.length === 0 ? (
        <p className="muted">{tc.noContacts}</p>
      ) : (
        <table className="table">
          <tbody>
            {card.contacts.map((c) => (
              <tr key={c.id}>
                <td>{c.name}</td>
                <td className="muted">{c.role ?? '—'}</td>
                <td>{c.phone ?? '—'}</td>
                <td>{c.email ?? '—'}</td>
                <td className="actions">
                  <button type="button" onClick={() => remove.mutate(c.id)}>
                    {t.common.remove}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <form className="inline-form" onSubmit={submit}>
        <input
          aria-label={`${tc.contactsTitle}: ${tc.contactName}`}
          placeholder={tc.contactName}
          required
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <input
          aria-label={`${tc.contactsTitle}: ${tc.contactRole}`}
          placeholder={tc.contactRole}
          value={form.role}
          onChange={(e) => setForm({ ...form, role: e.target.value })}
        />
        <input
          aria-label={`${tc.contactsTitle}: ${tc.phone}`}
          placeholder={tc.phone}
          type="tel"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <input
          aria-label={`${tc.contactsTitle}: ${tc.email}`}
          placeholder={tc.email}
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <button type="submit" disabled={add.isPending}>
          {t.common.add}
        </button>
      </form>
      <ErrorText error={add.error ?? remove.error} />
    </div>
  );
}

function AddressesSection({ card }: { card: Card }) {
  const add = useAddAddress(card.id);
  const update = useUpdateAddress(card.id);
  const remove = useRemoveAddress(card.id);
  const empty = {
    npCityName: '',
    npCityRef: '',
    npWarehouseName: '',
    npWarehouseRef: '',
    recipientName: card.fullName,
    recipientPhone: card.phone ?? '',
  };
  const [form, setForm] = useState(empty);
  const submit = (event: FormEvent) => {
    event.preventDefault();
    add.mutate(form, { onSuccess: () => setForm(empty) });
  };
  return (
    <div className="card">
      <h2>{tc.addressesTitle}</h2>
      {card.addresses.length === 0 ? (
        <p className="muted">{tc.noAddresses}</p>
      ) : (
        <table className="table">
          <tbody>
            {card.addresses.map(({ id, isDefault, ...a }) => (
              <tr key={id}>
                <td>
                  {a.npCityName ?? a.npCityRef}, {a.npWarehouseName ?? a.npWarehouseRef}
                </td>
                <td>
                  {a.recipientName} · {a.recipientPhone}
                </td>
                <td className="actions">
                  {isDefault ? (
                    <span className="badge ok">{tc.isDefault}</span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => update.mutate({ ...a, addressId: id, isDefault: true })}
                    >
                      {tc.makeDefault}
                    </button>
                  )}
                  <button type="button" onClick={() => remove.mutate(id)}>
                    {t.common.remove}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted hint">{tc.npHint}</p>
      <form className="form-grid" onSubmit={submit}>
        <Field form={form} set={setForm} name="npCityName" label={tc.city} />
        <Field form={form} set={setForm} name="npCityRef" label={tc.cityRef} required />
        <Field form={form} set={setForm} name="npWarehouseName" label={tc.warehouse} />
        <Field form={form} set={setForm} name="npWarehouseRef" label={tc.warehouseRef} required />
        <Field form={form} set={setForm} name="recipientName" label={tc.recipient} required />
        <Field
          form={form}
          set={setForm}
          name="recipientPhone"
          label={tc.recipientPhone}
          type="tel"
          required
        />
        <div className="actions">
          <button type="submit" disabled={add.isPending}>
            {t.common.add}
          </button>
        </div>
      </form>
      <ErrorText error={add.error ?? update.error ?? remove.error} />
    </div>
  );
}

/** The customer's orders, newest first (who may see orders: managers and the warehouse). */
function CustomerOrders({ customerId }: { customerId: string }) {
  const me = useCurrentUser();
  const allowed = ['orders.edit', 'shipments.manage'].some((p) => me.permissions.includes(p));
  const orders = useOrders({ customerId }, allowed);
  return (
    <div className="card">
      <h2>{tc.ordersTitle}</h2>
      {!allowed ? (
        <p className="muted">{tc.ordersNoAccess}</p>
      ) : (
        <QueryState query={orders}>
          {(page) =>
            page.items.length === 0 ? (
              <p className="muted">{t.orders.empty}</p>
            ) : (
              <OrdersTable orders={page.items} showCustomer={false} />
            )
          }
        </QueryState>
      )}
    </div>
  );
}

export function CustomerCardPage() {
  const { id = '' } = useParams();
  const card = useCustomerCard(id);
  return (
    <section className="wide">
      <Link to="/customers">{tc.back}</Link>
      <QueryState query={card}>
        {(c) => (
          // Keyed by updatedAt so forms reset to the saved values after every change.
          <div key={c.updatedAt}>
            <h1>
              {c.fullName} <span className="badge">{tc.types[c.type]}</span> {statusBadge(c.status)}
            </h1>
            <p className="muted">
              {tc.created} {formatDateTime(c.createdAt)} · {tc.viewLogged}
            </p>
            <StatusActions card={c} />
            <DetailsForm card={c} />
            {c.type === 'b2b' && <CompanySection card={c} />}
            <CustomerPricing card={c} />
            <CustomerLoyalty card={c} />
            <ContactsSection card={c} />
            <AddressesSection card={c} />
            <CustomerOrders customerId={c.id} />
          </div>
        )}
      </QueryState>
    </section>
  );
}
